import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, Checkbox, Stack, TextField } from '@mui/material';
import React, { ReactNode, SyntheticEvent } from 'react';

import InfoTooltip from '../InfoTooltip';

interface OptionItem {
  label: string;
  value: any;
  disabled?: boolean;
  key?: string;
}

interface FormComponentProps {
  clearIcon?: ReactNode;
  clearOnBlur?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  error: any;
  field: any;
  freeSolo?: boolean;
  helperText?: string;
  infoTooltip?: string;
  label?: string;
  multiple?: boolean;
  placeholder: string;
  onChange?: any;
  options: OptionItem[];
  readonly?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
}

const FormComponent: React.FC<FormComponentProps> = ({
  clearIcon,
  clearOnBlur,
  disabled = false,
  disableClearable = false,
  field,
  freeSolo = false,
  error,
  helperText = '',
  infoTooltip,
  label,
  multiple = false,
  onChange,
  options,
  placeholder,
  readonly: readOnly = false,
  required = false,
  size,
}) => {
  const getValueFromField = () => {
    // If this is a multiselect, get an array of options with a values that matches the form field values.
    if (multiple) {
      if (Array.isArray(field.value)) {
        return field.value
          .map((v: string) => {
            const option = options?.find(
              (option: OptionItem) => option.value === v,
            );

            if (option) {
              return option;
            }

            // If this is a freeSolo input, use the string value if we can't find a match
            if (freeSolo) {
              return v;
            }

            return null;
          })
          .filter(Boolean); // filter out undefined values
      }

      return [];
    }

    // If this is a single select autocomplete, get an option with a value that matches the form field value.
    return (
      options.find((option: OptionItem) => option.value === field.value) ?? null
    );
  };

  const value = getValueFromField();

  const getOptionDisabled = (option: OptionItem) => !!option.disabled;

  const getOptionLabel = (option: string | OptionItem) => {
    if (typeof option === 'string') {
      return option;
    }

    return option.label;
  };

  const getOptionKey = (option: string | OptionItem) => {
    if (typeof option === 'string') {
      return option;
    }
    const { label, key } = option;
    return key || label;
  };

  const isOptionEqualToValue = (option: OptionItem, item: OptionItem) => {
    if (option.value && item.value) {
      return option.value == item.value;
    }

    return option === item;
  };

  const renderInput = (params) => {
    return (
      <TextField
        {...params}
        error={error}
        fullWidth
        label={`${label}${required ? '*' : ''}`}
        name={field.name}
        inputProps={{
          ...params.inputProps,
          'data-testid': field.name,
          readOnly,
        }}
        helperText={helperText}
        placeholder={placeholder}
      />
    );
  };

  const onBlur = (event: SyntheticEvent): void => {
    const { value: inputValue } = event.target as HTMLInputElement;
    if (Array.isArray(value) && !value.includes(inputValue) && freeSolo) {
      onChange(event, [...value, inputValue]);
    }
    field.onBlur();
  };

  const renderOption = ({ key, ...props }, option, { selected }) => {
    return (
      <li key={option.key || option.label || key} {...props}>
        {multiple && (
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            style={{ marginRight: 8 }}
            checked={selected}
          />
        )}
        {option.label}
      </li>
    );
  };

  return (
    <Stack direction="row" alignItems="center">
      <Autocomplete
        clearIcon={clearIcon}
        clearOnBlur={clearOnBlur}
        disabled={disabled}
        disableClearable={disableClearable}
        disableCloseOnSelect={multiple}
        freeSolo={freeSolo}
        getOptionDisabled={getOptionDisabled}
        getOptionKey={getOptionKey}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        multiple={multiple}
        onBlur={onBlur}
        onChange={onChange}
        options={options}
        readOnly={readOnly}
        renderInput={renderInput}
        renderOption={renderOption}
        size={size || 'small'}
        sx={{ width: '100%' }}
        value={value}
      />
      {infoTooltip && <InfoTooltip infoMessage={infoTooltip} />}
    </Stack>
  );
};

export default FormComponent;
